import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';

import { GoodsShopControllerService } from '@app/api/goods-service/api/goodsShopController.service';
import { GoodsShopItem, STATUS_OPTIONS } from '../goods-shop.config';
import { GoodsShop } from '@app/api/goods-service/model/goodsShop';
import { PageRequestDTO } from '@app/api/goods-service/model/pageRequestDTO';
import { BlobConverterUtil } from '@app/shared/utils/blob-converter.util';
import { PermissionService } from '@app/core/services/permission.service';

@Component({
  selector: 'app-goods-shop-list',
  standalone: true,
  imports: [
    CommonModule,
    ToastModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    PaginatorModule,
    AppButtonComponent
  ],
  providers: [MessageService],
  templateUrl: './goods-shop-list.component.html',
  styleUrls: ['./goods-shop-list.component.css']
})
export class GoodsShopListComponent implements OnInit {
  // Data signals
  goodsShopList = signal<GoodsShopItem[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  
  // Search and filter signals
  searchTerm = signal('');
  statusFilter = signal<number | null>(null);
  
  // Pagination signals
  currentPage = signal(0);
  pageSize = signal(10);
  
  // Options
  statusOptions = signal(STATUS_OPTIONS);
  
  // Computed values
  filteredList = computed(() => {
    let list = this.goodsShopList();
    
    if (this.searchTerm()) {
      const term = this.searchTerm().toLowerCase();
      list = list.filter(item => 
        item.spuCode?.toLowerCase().includes(term) ||
        item.spuId?.toString().includes(term) ||
        item.shopId?.toString().includes(term)
      );
    }
    
    if (this.statusFilter() !== null) {
      list = list.filter(item => item.status === this.statusFilter());
    }
    
    return list;
  });

  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Permission flags - computed from PermissionService
  // Using specific submenu 'online_store' for channel permissions
  // Note: If goods-shop has its own submenu in the future, update this accordingly
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('channel.online_store.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('channel.online_store.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('channel.online_store.delete');
  });

  constructor(
    private goodsShopControllerService: GoodsShopControllerService,
    private router: Router,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadGoodsShopList();
  }

  /**
   * Load goods shop list from API
   */
  loadGoodsShopList(): void {
    const pageRequest: PageRequestDTO = {
      page: this.currentPage(),
      size: this.pageSize(),
      sortBy: 'id',
      ascending: false
    };
    
    const goodsShop: GoodsShop = {};
    
    this.loading.set(true);
    this.goodsShopControllerService.list7(goodsShop, pageRequest).subscribe({
      next: async (response: any) => {
        try {
          // Convert blob to JSON if needed
          const jsonData = await BlobConverterUtil.convertToJson(response);
          
          // Extract page content
          const content = BlobConverterUtil.extractPageContent(jsonData);
          
          // Map to GoodsShopItem format
          const items: GoodsShopItem[] = content.map((item: any) => ({
            id: item.id,
            spuId: item.spuId,
            spuCode: item.spuCode,
            shopId: item.shopId,
            status: item.status,
            creationDate: item.creationDate,
            lastUpdateDate: item.lastUpdateDate,
            createdBy: item.createdBy,
            lastUpdatedBy: item.lastUpdatedBy,
            deleteFlag: item.deleteFlag || 0,
            tenantId: item.tenantId || 0,
            objectVersionNumber: item.objectVersionNumber || 1
          }));
          
          this.goodsShopList.set(items);
          this.totalRecords.set(BlobConverterUtil.extractTotalElements(jsonData));
          this.loading.set(false);
        } catch (error) {
          console.error('Error processing goods shop list response:', error);
          this.loading.set(false);
        }
      },
      error: (error: any) => {
        console.error('Error loading goods shop list:', error);
        this.loading.set(false);
      }
    });
  }

  /**
   * Handle search input changes
   */
  onSearchChange(event: any): void {
    this.searchTerm.set(event.target.value);
  }

  /**
   * Handle status filter changes
   */
  onStatusFilterChange(event: any): void {
    const value = event.target.value;
    this.statusFilter.set(value === '' ? null : Number(value));
  }

  /**
   * Handle pagination changes
   */
  onPageChange(event: any): void {
    this.currentPage.set(event.page);
    this.pageSize.set(event.rows);
    this.loadGoodsShopList();
  }

  /**
   * Navigate to add/edit form
   */
  navigateToForm(mode: 'add' | 'edit', data?: any): void {
    // For now, using a default shop ID. In real app, this should come from context
    const defaultShopId = '1'; // TODO: Get from current context or user selection
    
    if (mode === 'add') {
      this.router.navigate(['/center-business/channel/goods-shop', defaultShopId]);
    } else {
      this.router.navigate(['/center-business/channel/goods-shop', defaultShopId, 'edit', data.id]);
    }
  }

  /**
   * Delete goods shop relationship
   */
  deleteGoodsShop(item: GoodsShopItem): void {
    if (confirm('Are you sure you want to delete this product store relationship?')) {
      this.loading.set(true);
      this.goodsShopControllerService.remove6(item.id!).subscribe({
        next: () => {
          this.loading.set(false);
          this.loadGoodsShopList();
        },
        error: () => {
          this.loading.set(false);
        }
      });
    }
  }

  /**
   * Clear search and filters
   */
  clearFilters(): void {
    this.searchTerm.set('');
    this.statusFilter.set(null);
  }

  /**
   * Get status label
   */
  getStatusLabel(status: number): string {
    const statusOption = this.statusOptions().find((option: any) => option.value === status);
    return statusOption ? statusOption.label : 'Unknown';
  }

  /**
   * Get status severity for styling
   */
  getStatusSeverity(status: number): string {
    return status === 1 ? 'success' : 'warning';
  }
}
